/**
 * Visual question-type library.
 *
 * Types still come from the server, so plugin registration remains the source
 * of truth. The client only adds discovery aids: recommendations, recent use,
 * categories, and a small example that explains each format at a glance.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api/client'
import { Button, Dialog, Input, useTabList } from '../../components/ui'
import { Icon, questionIcon } from '../../components/icons'
import { QuestionTypePreview } from './QuestionTypePreview'

const RECOMMENDED = new Set([
  'missing_number',
  'count_pictures',
  'compare_sizes',
  'shape_properties',
  'write_in_box',
  'tick_cross_pictures',
  'multiple_choice',
  'short_answer',
])
const RECENT_KEY = 'teachcraftai.recent-question-types'

export function AddQuestion({
  onPick,
  compact = false,
}: {
  onPick: (type: string) => void
  compact?: boolean
}) {
  const tabs = useTabList()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [view, setView] = useState<'recommended' | 'all'>('recommended')
  const [category, setCategory] = useState('Popular')
  const [recent, setRecent] = useState<string[]>(loadRecent)
  const types = useQuery({ queryKey: ['question-types'], queryFn: api.questionTypes })

  const availableTypes = types.data ?? []
  const categories = availableTypes.reduce<Record<string, typeof availableTypes>>((groups, type) => {
    const name = type.category || 'General'
    groups[name] = [...(groups[name] ?? []), type]
    return groups
  }, {})
  const categoryNames = Object.keys(categories)
  const search = query.trim().toLowerCase()
  const visibleTypes = availableTypes.filter((type) => {
    const matchesSearch =
      !search ||
      type.display_name.toLowerCase().includes(search) ||
      type.description.toLowerCase().includes(search) ||
      (type.category || 'General').toLowerCase().includes(search)
    if (!matchesSearch) return false
    if (search) return true
    if (category === 'Popular') return RECOMMENDED.has(type.type_key)
    if (category === 'All') return true
    return (type.category || 'General') === category
  })
  const recentTypes = recent
    .map((typeKey) => availableTypes.find((type) => type.type_key === typeKey))
    .filter((type) => type !== undefined)

  const choose = (type: string) => {
    const nextRecent = [type, ...recent.filter((entry) => entry !== type)].slice(0, 3)
    setRecent(nextRecent)
    saveRecent(nextRecent)
    resetAndClose()
    onPick(type)
  }

  const resetAndClose = () => {
    setOpen(false)
    setQuery('')
    setView('recommended')
    setCategory('Popular')
  }

  if (!types.data) return null

  return (
    <div className="tc-add-question">
      <Button
        size="sm"
        variant="primary"
        icon={<Icon.plus size={14} />}
        className="tc-add-question-trigger"
        onClick={() => setOpen(true)}
      >
        {compact ? 'Add question to this part' : 'Add question'}
      </Button>

      <Dialog
        open={open}
        title={
          <span className="tc-question-library-title">
            <span aria-hidden="true"><Icon.plus size={18} /></span>
            Add a question
          </span>
        }
        className="tc-question-library-dialog"
        onClose={resetAndClose}
      >
        <div className="tc-question-library-search">
          <span className="tc-question-library-search-icon" aria-hidden="true">
            <Icon.search size={17} />
          </span>
          <Input
            aria-label="Search question types"
            placeholder="Search question types"
            value={query}
            autoFocus
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>

        <div className="tc-question-library-tabs" ref={tabs.ref} onKeyDown={tabs.onKeyDown} role="tablist" aria-label="Question type views">
          <button
            type="button"
            role="tab"
            aria-selected={view === 'recommended'}
            className={view === 'recommended' ? 'is-active' : undefined}
            onClick={() => {
              setView('recommended')
              setCategory('Popular')
            }}
          >
            Recommended
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === 'all'}
            className={view === 'all' ? 'is-active' : undefined}
            onClick={() => {
              setView('all')
              setCategory('All')
            }}
          >
            All types
          </button>
        </div>

        <div className="tc-question-library">
          <nav className="tc-question-library-categories" aria-label="Question categories">
            {view === 'all' && (
              <button
                type="button"
                className={category === 'All' ? 'is-active' : undefined}
                onClick={() => setCategory('All')}
              >
                <span className="tc-category-symbol" aria-hidden="true"><Icon.grid size={17} /></span>
                All types
              </button>
            )}
            <button
              type="button"
              className={category === 'Popular' ? 'is-active' : undefined}
              onClick={() => setCategory('Popular')}
            >
              <span className="tc-category-symbol" aria-hidden="true"><Icon.star size={17} /></span>
              Popular
            </button>
            {categoryNames.map((name) => (
              <button
                key={name}
                type="button"
                className={category === name ? 'is-active' : undefined}
                onClick={() => {
                  setCategory(name)
                  if (view === 'recommended') setView('all')
                }}
              >
                <span className="tc-category-symbol" aria-hidden="true">
                  {questionIcon(categories[name][0]?.type_key ?? '', 17)}
                </span>
                {name}
              </button>
            ))}
          </nav>

          <div className="tc-question-library-content">
            {!search && (
              <div className="tc-question-library-recent">
                <strong>Recent</strong>
                <div>
                  {(recentTypes.length
                    ? recentTypes
                    : availableTypes.filter((type) => RECOMMENDED.has(type.type_key)).slice(0, 3)
                  ).map((type) => (
                    <button key={type.type_key} type="button" onClick={() => choose(type.type_key)}>
                      {questionIcon(type.type_key, 15)}
                      {type.display_name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="tc-question-library-results">
              {visibleTypes.map((type) => (
                <article className="tc-question-type-card" key={type.type_key}>
                  <div className="tc-question-type-copy">
                    <span className="tc-question-type-icon">
                      {questionIcon(type.type_key, 20)}
                    </span>
                    <strong>{type.display_name}</strong>
                    <p>{friendlyDescription(type.type_key, type.description)}</p>
                  </div>
                  <QuestionTypePreview type={type.type_key} />
                  {/* The visible word stays "Add" - the card beside it already
                      says what of - but every one of these was announced as a
                      bare "Add" to a screen reader, so the list was 20 identical
                      buttons. */}
                  <Button
                    size="sm"
                    className="tc-question-type-add"
                    aria-label={`Add ${type.display_name}`}
                    onClick={() => choose(type.type_key)}
                  >
                    Add
                  </Button>
                </article>
              ))}
              {visibleTypes.length === 0 && (
                <p className="tc-question-library-empty">
                  No question types match “{query}”.
                </p>
              )}
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  )
}

function loadRecent(): string[] {
  try {
    const value = JSON.parse(window.localStorage.getItem(RECENT_KEY) ?? '[]')
    return Array.isArray(value) ? value.filter((entry) => typeof entry === 'string').slice(0, 3) : []
  } catch {
    return []
  }
}

function saveRecent(types: string[]) {
  try {
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(types))
  } catch {
    // Recent choices are a convenience; adding a question must never depend on storage.
  }
}

function friendlyDescription(type: string, fallback: string) {
  const descriptions: Record<string, string> = {
    alphabet_handwriting: 'Students practise forming letters on familiar handwriting lines.',
    colouring: 'Students colour a selected picture directly on the printed page.',
    missing_number: 'Students write the missing number in a number sequence.',
    count_pictures: 'Students count the objects and write the number.',
    compare_sizes: 'Students compare objects and choose or mark the answer.',
    shape_properties: 'Students circle the correct choice for a picture or text prompt.',
    write_in_box: 'Students write in a square or complete a missing-letter word beside a picture.',
    tick_cross_pictures: 'Students place a tick or cross in the square under each picture.',
    multiple_choice: 'Students choose the correct answer from the options.',
    short_answer: 'Students write a short answer in the space provided.',
    drawing_response: 'Students draw and optionally colour their answer in a blank area.',
    fill_in_blanks: 'Students complete a sentence by writing the missing words.',
    matching: 'Students draw lines to connect each item with its matching partner.',
    numeric: 'Students solve a calculation and write the number in an answer box.',
    number_sequence_grid: 'Students continue or write a number sequence inside a grid.',
    odd_one_out: 'Students identify the picture or item that does not belong.',
    passage: 'Students read a passage before answering attached questions.',
    true_false: 'Students decide whether each statement is true or false.',
  }
  return descriptions[type] ?? fallback
}
